import { Traceable, TraceUrlHelper } from '@repo/shared/dtos/assistant'
import { TimeType } from '@repo/shared/dtos/generic'
import { useT } from '@repo/ui/hooks'
import { throttle } from 'lodash'
import { ClipboardCheckIcon, Database, DatabaseZap, Flame } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { ModalOptions, PreventLeaveDialog } from '@/components/PreventLeaveDialog'
import { buildRoute, isRoute, RouteNames } from '@/routes'
import { AvailableParams } from '@/routes/router'
import { storeActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { Task } from '@/store/task'

export const useLoadingMessages = (
  transationKeys: string[],
  isShown: boolean,
  interval: number = 1500,
): string => {
  const { t } = useT({ keyPrefix: 'assistant.loadingMessages' })

  const messages = useMemo(() => transationKeys.map((key) => t(key)), [transationKeys, t])
  const [message, setMessage] = useState('')
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const indexRef = useRef(0)

  const reset = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setMessage('')
    indexRef.current = 0
  }, [timerRef.current])

  useEffect(() => {
    if (!isShown) {
      reset()
      return
    }

    const updateMessage = () => {
      if (indexRef.current < messages.length) {
        const message = messages[indexRef.current]
        if (!message) return reset()
        setMessage(message)
        indexRef.current++
        timerRef.current = setTimeout(updateMessage, interval)
      }
    }

    updateMessage()

    return reset
  }, [isShown])

  return message
}

export type BreakpointName = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
const breakpointsMap = new Map<BreakpointName, number>([
  ['xs', 0],
  ['sm', 640],
  ['md', 768],
  ['lg', 1024],
  ['xl', 1280],
  ['2xl', 1536],
])

const getActiveBreakpoints = (width: number): BreakpointName[] => {
  const active: BreakpointName[] = []
  for (const [name, minWidth] of breakpointsMap) {
    if (width >= minWidth) {
      active.push(name)
    }
  }
  return active
}

export function useBreakpoint(): {
  activeBreakpoints: BreakpointName[]
  /**
   * True if the viewport is xs, sm, or md, but not lg, xl, or 2xl.
   */
  isShortViewport: boolean
  isXS: boolean
} {
  const getWindowWidth = useCallback((): number => window.innerWidth, [])
  const [activeBreakpoints, setActiveBreakpoints] = useState<BreakpointName[]>(() =>
    getActiveBreakpoints(getWindowWidth()),
  )
  const throttledHandleResize = useRef(
    throttle(() => {
      setActiveBreakpoints(getActiveBreakpoints(getWindowWidth()))
    }, 100),
  ).current
  useEffect(() => {
    throttledHandleResize()
    window.addEventListener('resize', throttledHandleResize)
    return () => {
      window.removeEventListener('resize', throttledHandleResize)
      throttledHandleResize.cancel?.()
    }
  }, [throttledHandleResize])
  const isShortViewport = activeBreakpoints.some(
    (bp) =>
      (bp === 'xs' || bp === 'sm' || bp === 'md') &&
      !activeBreakpoints.includes('lg') &&
      !activeBreakpoints.includes('xl') &&
      !activeBreakpoints.includes('2xl'),
  )
  return {
    activeBreakpoints,
    isShortViewport,
    isXS: activeBreakpoints.length === 1,
  }
}

const CLAMP_HEIGHT_TOLERANCE = 1 // For rounding tolerance
const RESIZE_THROTTLE_MS = 300

export function useTextClamp<T extends HTMLElement>({
  lines,
  dependency,
}: {
  lines: number
  dependency?: unknown
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const textRef = useRef<T>(null)

  const checkClampState = useCallback(() => {
    const el = textRef.current
    if (!el) return

    const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
    const clampHeight = lineHeight * lines
    setIsClamped(el.scrollHeight > clampHeight + CLAMP_HEIGHT_TOLERANCE)
  }, [lines])

  const throttledCheckClampState = useMemo(
    () => throttle(checkClampState, RESIZE_THROTTLE_MS),
    [checkClampState],
  )

  useEffect(() => {
    checkClampState()
  }, [dependency, checkClampState])

  useEffect(() => {
    window.addEventListener('resize', throttledCheckClampState)
    return () => {
      window.removeEventListener('resize', throttledCheckClampState)
      throttledCheckClampState.cancel?.()
    }
  }, [throttledCheckClampState])

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  return {
    isExpanded,
    isClamped,
    textRef,
    toggleExpanded,
    setIsExpanded,
  }
}

export type OnTaskChange = (
  action: 'delete' | 'archive' | 'unarchive' | 'updateDueDate',
) => (taskId: string, dueDate?: TimeType) => void

export function useTaskChangeHandler(task: Task): OnTaskChange
export function useTaskChangeHandler(tasks: Task[]): OnTaskChange
export function useTaskChangeHandler(param: Task | Task[]): OnTaskChange {
  const tasks = Array.isArray(param) ? param : [param]
  const dispatch = useStoreDispatch()
  return useCallback<OnTaskChange>(
    (action) => (taskId, dueDate) => {
      const task = tasks.find(({ id }) => id === taskId)
      if (!task) return
      switch (action) {
        case 'delete':
          void dispatch(storeActions.task.delete({ task }))
          break
        case 'archive':
          void dispatch(storeActions.task.archive({ task }))
          break
        case 'unarchive':
          void dispatch(storeActions.task.unarchive({ task }))
          break
        case 'updateDueDate':
          void dispatch(
            storeActions.task.updateDueDate({
              taskId: task.id,
              dueDate,
            }),
          )
          break
      }
    },
    [tasks],
  )
}

type ViewReturnParams = {
  route: string
  navigate: () => void
}

export function useView() {
  const { organizationId: currentOrganizationId, beneficiaryId: currentBeneficiaryId } =
    useParams<AvailableParams>()
  const goTo = useNavigate()
  const location = useLocation()
  const pathname = useMemo(() => location.pathname, [location.pathname])

  const getRoute = useCallback(
    (...args: string[]): string => {
      const baseRoute: string[] = []
      if (currentOrganizationId) baseRoute.push(RouteNames.Organizations, currentOrganizationId)

      const route = `/${baseRoute.join('/')}${args.length ? '/' + args.join('/') : ''}`
      return route
    },
    [currentOrganizationId],
  )

  const dashboard = useCallback(
    (organizationId: string): ViewReturnParams => {
      const route = buildRoute(RouteNames.Organizations, organizationId)
      return {
        route,
        navigate: () => {
          goTo(route, { replace: true })
        },
      }
    },
    [pathname, getRoute],
  )

  const beneficiaryProfile = useCallback(
    (beneficiaryId: string): ViewReturnParams => {
      const route = getRoute(RouteNames.Beneficiaries, beneficiaryId)
      return {
        route,
        navigate: () => {
          goTo(route, { replace: true })
        },
      }
    },
    [pathname, getRoute],
  )

  const tasks = useCallback(
    (beneficiaryId: string): ViewReturnParams => {
      const route = getRoute(RouteNames.Beneficiaries, beneficiaryId, RouteNames.BeneficiaryTasks)
      return {
        route,
        navigate: () => {
          if (isRoute(RouteNames.BeneficiaryTasks, pathname)) return
          goTo(route, { replace: true })
        },
      }
    },
    [pathname, getRoute],
  )

  const beneficiaryChat = useCallback(
    (beneficiaryId: string) =>
      (chatSessionId: string): ViewReturnParams => {
        const route = getRoute(
          RouteNames.Beneficiaries,
          beneficiaryId,
          RouteNames.BeneficiaryChat,
          chatSessionId,
        )
        return {
          route,
          navigate: () => {
            if (pathname.includes(chatSessionId)) return
            goTo(route, { replace: true })
          },
        }
      },
    [pathname, getRoute],
  )

  return {
    // NOTE: if beneficiaryId is not provided, we use the currentBeneficiaryId
    beneficiary: (beneficiaryId = currentBeneficiaryId) => {
      if (!beneficiaryId) throw new Error('Beneficiary ID is required')
      return {
        profile: beneficiaryProfile(beneficiaryId),
        tasks: tasks(beneficiaryId),
        chat: beneficiaryChat(beneficiaryId),
      }
    },
    // NOTE: if organizationId is not provided, we use the currentOrganizationId
    dashboard: (organizationId = currentOrganizationId) => {
      if (!organizationId) throw new Error('Organization ID is required')
      return dashboard(organizationId)
    },
  }
}

export function useTrace({ traceUrl }: Traceable) {
  const { tc } = useT()
  const [isShown, setIsShown] = useState(false)

  useEffect(() => {
    function keyDownHandler(e: globalThis.KeyboardEvent) {
      if (traceUrl && e.key === 'Control') {
        e.preventDefault()
        setIsShown((state) => !state)
      }
    }
    // Remove any existing listener before adding a new one
    document.removeEventListener('keydown', keyDownHandler)
    document.addEventListener('keydown', keyDownHandler)
    return () => document.removeEventListener('keydown', keyDownHandler)
  }, [traceUrl])

  const Trace = () => {
    if (!isShown) return null
    const cached = TraceUrlHelper.isTraceUrlCached(traceUrl)
    const inDb = TraceUrlHelper.isTraceUrlInDb(traceUrl)
    return (
      <a
        href={traceUrl}
        className="flex flex-shrink-0 items-center gap-1 text-xs underline"
        target="_blank"
      >
        {tc('seeTrace')}{' '}
        {cached ? (
          <DatabaseZap className="size-4" />
        ) : inDb ? (
          <Database className="size-4" />
        ) : (
          <Flame className="size-4 text-red-500" />
        )}
      </a>
    )
  }

  return { Trace }
}

type CopyFn = (text: string) => Promise<boolean> // Returns true on success, false on failure
export function useCopyToClipboard(messageOnSuccess: string): {
  copy: CopyFn
  isCopied: boolean
} {
  const [isCopied, setIsCopied] = useState(false)

  const copy: CopyFn = async (text) => {
    if (!navigator?.clipboard) {
      console.warn('Clipboard not supported')
      return false
    }

    // Try to save to clipboard
    try {
      await navigator.clipboard.writeText(text)
      setIsCopied(true)
      return true
    } catch (error) {
      console.warn('Copy failed', error)
      setIsCopied(false)
      return false
    }
  }

  // Reset the "isCopied" state after a delay
  useEffect(() => {
    if (!isCopied) return
    const timeout = setTimeout(() => {
      setIsCopied(false)
    }, 2000) // Reset after 2 seconds

    toast(messageOnSuccess, {
      closeButton: true,
      dismissible: true,
      icon: <ClipboardCheckIcon className="size-5 text-green-500" />,
    })

    return () => {
      clearTimeout(timeout)
    }
  }, [isCopied])

  return { copy, isCopied }
}

export function usePreventLeave(
  condition: boolean,
): (p: { modalOptions: ModalOptions }) => JSX.Element {
  const blocker = useBlocker(condition)
  const [open, setOpen] = useState(condition)

  useEffect(() => {
    if (!condition) return

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      if ('returnValue' in e) e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [condition])

  useEffect(() => {
    if (blocker.state === 'blocked') setOpen(true)
    else setOpen(false)
  }, [blocker])

  useEffect(() => {
    if (!open) blocker.proceed?.()
  }, [open])

  return function ({ modalOptions }: { modalOptions: ModalOptions }) {
    return <PreventLeaveDialog {...modalOptions} modalHandler={{ open, setOpen }} />
  }
}

export function useDelayedLoader(isLoading: boolean, minDelay: number = 500) {
  const [isLoadingShown, setIsLoadingShown] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const clean = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
    clean()

    if (isLoading) {
      setIsLoadingShown(true)
    } else {
      timeoutRef.current = setTimeout(() => {
        setIsLoadingShown(false)
        timeoutRef.current = null
      }, minDelay)
    }

    return clean
  }, [isLoading, minDelay])

  return isLoadingShown
}

export function useWait(condition: boolean, delay: number = 500) {
  const [isEnded, setIsEnded] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const clean = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    if (condition) {
      timeoutRef.current = setTimeout(() => setIsEnded(true), delay)
    } else {
      clean()
      setIsEnded(false)
    }

    return clean
  }, [condition, delay])

  return isEnded
}
